import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { BOOKING_BY_ID, GET_STUDIO_EXACT_LOCATION } from '../graphql/queries/bookingQueries';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import awsConfig from '../config/aws-config';
import { canShowExactLocationForStatus } from '../util/locationPrivacy';
import { useSelector } from 'react-redux';

export const useFetchUserBookingDetails = (bookingId) => {
    const userId = useSelector((state) => state?.auth?.user?.id);
    const { data, loading, error } = useQuery(BOOKING_BY_ID, {
      variables: { id: bookingId },
      skip: !bookingId, 
      fetchPolicy: 'cache-and-network',
    });

    const booking = data?.getBooking;
    const canFetchExactLocation =
      canShowExactLocationForStatus(booking?.status) ||
      (booking?.studioOwnerID && booking.studioOwnerID === userId);

    const {
      data: exactLocationData,
      loading: exactLocationLoading,
      error: exactLocationError
    } = useQuery(GET_STUDIO_EXACT_LOCATION, {
      variables: {
        studioID: booking?.studioID,
        bookingID: booking?.id,
      },
      skip: !booking?.studioID || !booking?.id || !canFetchExactLocation,
      fetchPolicy: 'network-only',
    });

    // Process all booking data including image URLs
    const processedBooking = useMemo(() => {
      if (!data?.getBooking) return null;
  
      const booking = data.getBooking;
      const exactLocation = exactLocationData?.getStudioExactLocation || null;
      
      if (!booking.Studio) return booking;
  
      const studioWithProcessedImages = {
          ...booking.Studio,
          processedImages: []
      };

      if (booking.Studio.images && booking.Studio.images.length > 0) {
        try {
          studioWithProcessedImages.processedImages = booking.Studio.images.map(imageKey => {
            if (!imageKey) return '';
            
            try {
              return getCloudFrontImageUrl(imageKey, { 
                width: 1200,
                height: 800, 
                fit: 'cover' 
              });
            } catch (error) {
              try {
                const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
                return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
              } catch (fallbackError) {
                return '';
              }
            }
          });
        } catch (error) {
          studioWithProcessedImages.processedImages = [''];
        }
      }

      // Process booking user's profile picture
      let processedBookingUser = { ...booking.User };
      if (booking.User?.profilePicture) {
        try {
          processedBookingUser.userProfilePicture = getCloudFrontImageUrl(booking.User.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedBookingUser.userProfilePicture = null;
        }
      } else {
        processedBookingUser.userProfilePicture = null;
      }

      // Process studio owner's profile picture
      let processedUser = { ...booking.Studio.User };
      if (booking.Studio.User?.profilePicture) {
        try {
          processedUser.studioOwnerProfilePicture = getCloudFrontImageUrl(booking.Studio.User.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedUser.studioOwnerProfilePicture = null;
        }
      } else {
        processedUser.studioOwnerProfilePicture = null;
      }

      if (booking.Studio.User) {
        let userFirstName = booking.Studio.User.firstName;
        let userLastName = booking.Studio.User.lastName;
        
        processedUser.firstName = userFirstName;
        processedUser.lastName = userLastName;
      } else {
        processedUser.firstName = null;
        processedUser.lastName = null;
      }

      // Process creativePackages - handle both JSON string and parsed array
      let processedCreativePackages = [];
      if (booking.creativePackages) {
        try {
          // If it's a string, parse it; otherwise use as-is
          if (typeof booking.creativePackages === 'string') {
            processedCreativePackages = JSON.parse(booking.creativePackages);
          } else if (Array.isArray(booking.creativePackages)) {
            processedCreativePackages = booking.creativePackages;
          }
        } catch (error) {
          processedCreativePackages = [];
        }
      }

      return {
          ...booking,
          exactLocation,
          creativePackages: processedCreativePackages,
          User: processedBookingUser,
          Studio: {
            ...studioWithProcessedImages,
            ...(exactLocation && canFetchExactLocation ? exactLocation : {}),
            User: processedUser
          }
      };
    }, [data, bookingId, exactLocationData, canFetchExactLocation]);

    return {
      bookingData: processedBooking,
      loading: loading || (canFetchExactLocation && exactLocationLoading),
      error: error || (canFetchExactLocation ? exactLocationError : null)
    };
};
